import styled from 'styled-components';

import { ACQ, ANIM, COLORS, MOBILE, STATUS_COLORS } from '@/nova/theme/tokens';
import { Text } from '@/nova/components';

/* ── Layout ─────────────────────────────────────────────────────────────── */

export const Dashboard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  background: ${ACQ.cardOuter};
  border-radius: 16px;
  padding: 12px 14px;
`;

export const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

/**
 * Integrated toolbar action (Refresh / Export): reads as part of the control strip
 * (same cardInner surface + hairline as the date fields), not a bordered secondary
 * CTA. The icon uses `currentColor`, so it brightens with the label on hover.
 */
export const ToolButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid ${ACQ.hairline};
  background: ${ACQ.cardInner};
  color: ${COLORS.text};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ease, color ${ANIM.INTERACT_MS}ms ease,
    border-color ${ANIM.INTERACT_MS}ms ease;

  &:hover,
  &:focus-visible {
    background: ${COLORS.surfaceMuted};
    color: ${COLORS.heading};
    border-color: ${ACQ.divider};
    outline: none;
  }
`;

/* ── Period selector ────────────────────────────────────────────────────── */

export const PillGroup = styled.div`
  display: inline-flex;
  gap: 4px;
  background: ${ACQ.cardInner};
  border-radius: 10px;
  padding: 4px;
`;

export const Pill = styled.button<{ $active?: boolean }>`
  appearance: none;
  border: 0;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 7px;
  white-space: nowrap;
  color: ${(p) => (p.$active ? COLORS.ctaSolidText : COLORS.textMuted)};
  background: ${(p) => (p.$active ? ACQ.orange : 'transparent')};
  transition: background ${ANIM.INTERACT_MS}ms ease, color ${ANIM.INTERACT_MS}ms ease;

  &:hover {
    color: ${(p) => (p.$active ? COLORS.ctaSolidText : COLORS.text)};
  }
`;

export const DateFields = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const DateField = styled.input`
  font: inherit;
  font-size: 13px;
  color: ${COLORS.text};
  background: ${ACQ.cardInner};
  border: 1px solid ${ACQ.hairline};
  border-radius: 8px;
  padding: 7px 10px;
  color-scheme: dark;

  &:focus {
    outline: none;
    border-color: ${ACQ.orangeBorder};
  }
`;

export const DateArrow = styled.span`
  color: ${COLORS.textFaint};
  font-size: 13px;
`;

/* ── KPI area ───────────────────────────────────────────────────────────── */

export const KpiArea = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 340px) 1fr;
  gap: 12px;

  ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

export const HeroPanel = styled.div`
  background: ${ACQ.cardOuter};
  border-radius: 16px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const HeroHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const HeroLabel = styled(Text)`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
`;

export const ProvisionalChip = styled(Text)`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${ACQ.gold};
  background: ${ACQ.goldBorder};
  border-radius: 999px;
  padding: 2px 8px;
`;

export const HeroValue = styled.div`
  font-size: 34px;
  font-weight: 700;
  line-height: 1.05;
  color: ${COLORS.heading};
  font-variant-numeric: tabular-nums;
`;

export const SplitBar = styled.div`
  display: flex;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: ${COLORS.metricTileBg};
  gap: 2px;
`;

export const SplitSeg = styled.div<{ $share: number; $tone: 'cpa' | 'rev' }>`
  flex: ${(p) => Math.max(0, p.$share)} 1 0;
  min-width: ${(p) => (p.$share > 0 ? '4px' : '0')};
  background: ${(p) => (p.$tone === 'cpa' ? ACQ.orange : STATUS_COLORS.positive)};
`;

export const SplitLegend = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

export const Swatch = styled.span<{ $color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 3px;
  background: ${(p) => p.$color};
  flex: none;
`;

export const LegendValue = styled.span`
  color: ${COLORS.heading};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

/* ── Sparkline ──────────────────────────────────────────────────────────── */

export const SparkWrap = styled.div`
  margin-top: 8px;
  height: 28px;
`;

/* ── Chart panel ────────────────────────────────────────────────────────── */

export const Panel = styled.div`
  background: ${ACQ.cardOuter};
  border-radius: 16px;
  padding: 16px;
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
`;

export const PanelTitle = styled(Text)`
  font-size: 15px;
  font-weight: 700;
  color: ${COLORS.heading};
`;

export const PanelSub = styled(Text)`
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

export const Controls = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Segmented = styled.div`
  display: inline-flex;
  gap: 2px;
  background: ${ACQ.cardInner};
  border-radius: 9px;
  padding: 3px;
`;

export const SegBtn = styled.button<{ $active?: boolean }>`
  appearance: none;
  border: 0;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 6px;
  white-space: nowrap;
  color: ${(p) => (p.$active ? COLORS.heading : COLORS.textMuted)};
  background: ${(p) => (p.$active ? COLORS.surfaceMuted : 'transparent')};

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export const Select = styled.select`
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.text};
  background: ${ACQ.cardInner};
  border: 1px solid ${ACQ.hairline};
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${ACQ.orangeBorder};
  }
`;

export const MetricChips = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const MetricChip = styled.button<{ $active?: boolean; $color: string }>`
  appearance: none;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 999px;
  white-space: nowrap;
  color: ${(p) => (p.$active ? COLORS.heading : COLORS.textMuted)};
  background: ${(p) => (p.$active ? COLORS.surfaceMuted : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? p.$color : ACQ.hairline)};

  &:hover {
    color: ${COLORS.text};
  }
`;

export const ChartBody = styled.div`
  width: 100%;
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  margin-top: 10px;
`;

export const LegendChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

/* ── Breakdown table wrapper reuses the existing ReportsTab.styles table ──── */

export const TableHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 4px;
`;
